import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import MyUserContext from './../myUserContext.js';
import PageHeaderContext from './../pageHeaderContext.js';

// Sticky top bar: back chevron (mobile-only) | title | optional rightSlot.
// Lives inside the page content so it participates in that page's layout
// and transitions, instead of hanging as a global overlay. Marks itself
// as active in PageHeaderContext so the global back button in MainPage
// hides while this component is mounted (no duplicate). The flag flip
// happens in useLayoutEffect — synchronous before paint — otherwise the
// global button briefly renders on the first frame and the user sees it
// swap for the PageHeader's own back button.
const PageHeader = ({ title, rightSlot }) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 48em)');
  const myUser = useContext(MyUserContext);
  const { setActive } = useContext(PageHeaderContext);

  useLayoutEffect(() => {
    if (isDesktop) return undefined;
    setActive(true);
    return () => setActive(false);
  }, [setActive, isDesktop]);

  // Built-in ⋯ menu with "Copy link" — rendered as the default rightSlot
  // on every PageHeader that doesn't supply its own. Pages that pass
  // their own rightSlot (ArtPage, PersonalArtEvents, CardEventArtChat)
  // keep ownership and are responsible for including Copy link themselves.
  const [defaultMenuOpen, setDefaultMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef(null);
  useEffect(() => () => { if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current); }, []);
  useEffect(() => {
    if (!defaultMenuOpen) return undefined;
    const onDown = (e) => {
      if (e.target.closest && e.target.closest('[data-ph-default-menu]')) return;
      setDefaultMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [defaultMenuOpen]);
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 1200);
    } catch {}
  };
  const language = myUser?.myUserSettings?.lang || 'ru';
  const defaultRightSlot = (
    <div className="relative" data-ph-default-menu>
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); setDefaultMenuOpen(v => !v); }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDefaultMenuOpen(v => !v); } }}
        className="cursor-pointer select-none px-2"
        style={{ fontSize: 22, lineHeight: 1 }}
        aria-label={language === 'ru' ? 'Меню' : 'Menu'}
      >
        &#x22EF;
      </div>
      {defaultMenuOpen && (
        <div
          className="absolute right-0 mt-2 w-56 rounded shadow-lg"
          style={{ background: 'var(--mantine-color-body)', border: '1px solid var(--mantine-color-default-border)', zIndex: 60 }}
        >
          <div
            className="px-4 py-2 cursor-pointer text-sm"
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--mantine-color-default-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.background = ''}
            onClick={() => { handleCopyLink(); setDefaultMenuOpen(false); }}
          >
            {language === 'ru'
              ? (copied ? 'Ссылка скопирована ✔' : 'Скопировать ссылку')
              : (copied ? 'Link copied ✔' : 'Copy link')}
          </div>
        </div>
      )}
    </div>
  );
  const effectiveRightSlot = rightSlot !== undefined ? rightSlot : defaultRightSlot;

  // Desktop already has AppShell.Header as top chrome; stacking a
  // PageHeader (plus its 44px spacer) on top of that just creates a
  // dead 44px gap between AppShell.Header and the actual page content.
  // Skip PageHeader on desktop — mobile is the only place that needs it.
  if (isDesktop) return null;

  // 44 px bar pinned to the viewport. Fixed (not sticky) because sticky
  // was riding the AppShell.Main reflow — mobile URL-bar collapse and
  // any flex-pressure on the content column were visually shrinking it.
  // A spacer sibling of the same height reserves the layout space so
  // content below starts at the right spot.
  const headerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 50,
    background: 'var(--mantine-color-body)',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    height: 48,
    minHeight: 48,
    flexShrink: 0,
    padding: '0 4px',
  };
  return (
    <>
    <div style={headerStyle}>
      {/* Hide the back chevron when there's no in-tab history
          (Instagram pattern — direct-landed pages don't show back).
          navCount is the same session-tracked counter the global back
          button uses in MainPage. */}
      {!isDesktop && (() => {
        let navCount = 0;
        try { navCount = parseInt(sessionStorage.getItem('_navCount') || '0', 10); } catch {}
        return navCount > 0;
      })() ? (
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          title="Back"
          style={{
            width: 48,
            height: 48,
            flex: '0 0 48px',
            padding: 0,
            paddingLeft: 2,
            border: 'none',
            background: 'transparent',
            color: 'var(--mantine-color-text)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          {/* Instagram-style chevron, inline SVG so it renders crisp on
              any theme/background without filter hacks. */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      ) : (
        // No back chevron: for text titles keep a 48px left spacer so
        // the centered title still looks symmetric to the right spacer;
        // for custom-node titles (e.g. a search input) drop the spacer
        // so the input can stretch to the viewport edge.
        <div style={typeof title === 'string' ? { width: 48, flex: '0 0 48px' } : { width: 0, flex: '0 0 0' }} />
      )}
      <div
        style={typeof title === 'string'
          ? {
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 18,
              fontWeight: 600,
              textAlign: 'center',
            }
          : {
              // Non-string title (e.g. a TextInput used as a search bar
              // on /addEvent). Just reserve the flex-1 slot; let the
              // child own its own styling.
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
            }
        }
        title={typeof title === 'string' ? title : undefined}
      >
        {title}
      </div>
      {/* Right slot: caller-supplied if present, otherwise the built-in
          ⋯ menu with Copy link. Null means the caller explicitly wants
          no right slot — fall back to a symmetric spacer so the title
          stays centered under a text title. */}
      {effectiveRightSlot
        ? <div style={{ flex: '0 0 auto', paddingRight: 4 }}>{effectiveRightSlot}</div>
        // Non-string title (custom node like a search input): let it
        // stretch to the right edge, no reserved right spacer.
        : typeof title === 'string'
          ? <div style={{ flex: '0 0 48px', width: 48 }} />
          : <div style={{ flex: '0 0 0', width: 0 }} />}
    </div>
    {/* Layout spacer so content below starts at the real top of the
        document instead of underneath the fixed bar. */}
    <div style={{ height: 48, flexShrink: 0 }} aria-hidden="true" />
    </>
  );
};

export default PageHeader;
